// Shared invoice model builder.
//
// Produces the exact object shape that <InvoiceDisplay /> expects, derived from a
// registration document + camp settings. This mirrors the mapping used on the
// Registration Details page so that any consumer (invoice PDF, AR reminders) renders
// an identical invoice and computes an identical balance.

import { getPrimaryCamperName } from './invoiceEmail';

export const calculateInvoiceNights = (arrivalDate: any, departureDate: any): number => {
  if (!arrivalDate || !departureDate) return 0;
  const arrival = new Date(arrivalDate);
  const departure = new Date(departureDate);
  const timeDiff = departure.getTime() - arrival.getTime();
  const nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
  return nights > 0 ? nights : 0;
};

// Build the registration fee line items, preferring live rates + attending campers
// (matching the Financial Summary), with a legacy fallback for older records.
export const buildInvoiceRegistrationFeeRows = (registration: any, campSettings: any) => {
  if (!registration) return [];

  if (
    registration.attendingCampers &&
    registration.attendingCampers.length > 0 &&
    campSettings?.rates?.registration
  ) {
    return registration.attendingCampers.map((camper: any) => {
      let nights = camper.nights;
      if (!nights && registration?.campDates?.arrivalDate && registration?.campDates?.departureDate) {
        nights = calculateInvoiceNights(registration.campDates.arrivalDate, registration.campDates.departureDate);
      }
      nights = nights || 0;

      const isFullCamp = nights >= 5;
      let fee = 0;
      let category = 'Adult';

      if (camper.age === 'Adult') {
        category = 'Adult';
        const rate = isFullCamp
          ? (campSettings.rates.registration.adult?.weekly || 0)
          : (campSettings.rates.registration.adult?.nightly || 0);
        fee = isFullCamp ? rate : rate * nights;
      } else if (camper.age === 'AdultStudent') {
        category = 'Adult Student';
        const rate = isFullCamp
          ? (campSettings.rates.registration.adultStudent?.weekly || 0)
          : (campSettings.rates.registration.adultStudent?.nightly || 0);
        fee = isFullCamp ? rate : rate * nights;
      } else if (camper.age === 'YouthChildren') {
        category = 'Youth/Children';
        const rate = isFullCamp
          ? (campSettings.rates.registration.youthAndChildren?.weekly || 0)
          : (campSettings.rates.registration.youthAndChildren?.nightly || 0);
        fee = isFullCamp ? rate : rate * nights;
      }

      return {
        name: `${camper.firstName || ''} ${camper.lastName || ''}`.trim() || 'Camper',
        category,
        nights,
        fee,
      };
    });
  }

  return [
    ...((registration as any).financial?.registrationFees?.adults || []).map((adult: any) => ({
      name: adult.name,
      category: 'Adult',
      nights: adult.nights || 0,
      fee: adult.fee,
    })),
    ...((registration as any).financial?.registrationFees?.adultStudents || []).map((student: any) => ({
      name: student.name,
      category: 'Adult Student',
      nights: student.nights || 0,
      fee: student.fee,
    })),
    ...((registration as any).financial?.registrationFees?.youthChildren || []).map((youth: any) => ({
      name: youth.name,
      category: 'Youth/Children',
      nights: youth.nights || 0,
      fee: youth.fee,
    })),
  ];
};

// Replicates the balance math inside <InvoiceDisplay /> so the number shown in an
// email body is guaranteed to equal the total on the attached PDF.
export const computeInvoiceBalance = (model: any): number => {
  if (!model || !model.accommodationFees) return 0;

  const accommodationFee = Number(model.accommodationFees.fee) || 0;
  const accommodationOverride = Number(model.accommodationFees.override?.amount) || 0;
  const accommodationTotal = accommodationFee + accommodationOverride;

  const registrationBaseTotal = (model.registrationFees || []).reduce(
    (sum: number, fee: any) => sum + (Number(fee.fee) || 0),
    0
  );
  const registrationOverride = Number(model.registrationOverride?.amount) || 0;
  const registrationTotal = registrationBaseTotal + registrationOverride;

  const mealFees = model.mealFees;
  const mealFullCampAdultTotal = (mealFees?.fullCampAdult?.quantity || 0) * (mealFees?.fullCampAdult?.unitPrice || 0);
  const mealFullCampChildTotal = (mealFees?.fullCampChild?.quantity || 0) * (mealFees?.fullCampChild?.unitPrice || 0);
  const mealDailyAdultTotal = (mealFees?.dailyAdult?.quantity || 0) * (mealFees?.dailyAdult?.unitPrice || 0);
  const mealDailyChildTotal = (mealFees?.dailyChild?.quantity || 0) * (mealFees?.dailyChild?.unitPrice || 0);
  const mealFeesOverride = Number(mealFees?.override?.amount) || 0;
  const mealFeesTotal =
    mealFullCampAdultTotal + mealFullCampChildTotal + mealDailyAdultTotal + mealDailyChildTotal + mealFeesOverride;

  const fridgeRental = Number(model.otherFees?.fridgeRental) || 0;
  const donation = Number(model.otherFees?.donation) || 0;
  const customFee = Number(model.otherFees?.customFee) || 0;
  const otherFeesArrayTotal = (model.otherFees?.otherFees || []).reduce(
    (sum: number, fee: any) => sum + (Number(fee.amount) || 0),
    0
  );
  const otherFeesOverride = Number(model.otherFees?.override?.amount) || 0;
  const otherFeesTotal = fridgeRental + donation + customFee + otherFeesArrayTotal + otherFeesOverride;

  const subtotal = accommodationTotal + registrationTotal + mealFeesTotal + otherFeesTotal;
  const paymentAmount = Number(model.paymentAmount) || 0;
  return subtotal - paymentAmount;
};

// Computes the grand total of all charges for a registration using LIVE camp
// rates (registration fees = nights x rate per attending camper, meal fees =
// quantity x rate), plus accommodation, other fees, and only the overrides that
// are enabled. This mirrors the Financial Summary / Registration Details math
// exactly, so it must be given campSettings (settings/camp) to be accurate.
export const computeRegistrationTotal = (registration: any, settings: any): number => {
  let grandTotal = 0;

  // Accommodation fees
  if (registration?.financial?.accommodationFees?.assignedAccommodation && settings?.rates?.accommodation) {
    grandTotal += Number(registration.financial.accommodationFees.accommodationFee) || 0;
  }

  // Registration fees (recomputed from live rates and current campers/nights)
  if (Array.isArray(registration?.attendingCampers) && settings?.rates?.registration) {
    registration.attendingCampers.forEach((camper: any) => {
      const nights = camper.nights || calculateInvoiceNights(registration?.campDates?.arrivalDate, registration?.campDates?.departureDate);
      const isFullCamp = nights >= 5;

      let rate = 0;
      if (camper.age === 'Adult' && settings.rates.registration.adult) {
        rate = isFullCamp ? (settings.rates.registration.adult.weekly || 0) : (settings.rates.registration.adult.nightly || 0) * nights;
      } else if (camper.age === 'AdultStudent' && settings.rates.registration.adultStudent) {
        rate = isFullCamp ? (settings.rates.registration.adultStudent.weekly || 0) : (settings.rates.registration.adultStudent.nightly || 0) * nights;
      } else if (camper.age === 'YouthChildren' && settings.rates.registration.youthAndChildren) {
        rate = isFullCamp ? (settings.rates.registration.youthAndChildren.weekly || 0) : (settings.rates.registration.youthAndChildren.nightly || 0) * nights;
      }
      grandTotal += rate;
    });
  }

  // Meal fees (recomputed from live rates) - check new path then legacy
  if (settings?.rates?.meals) {
    const fullCampAdultRate = settings.rates.meals.fullCampAdult || 0;
    const fullCampChildRate = settings.rates.meals.fullCampChild || 0;
    const dailyAdultRate = settings.rates.meals.dailyAdult || 0;
    const dailyChildRate = settings.rates.meals.dailyChild || 0;

    const meals = registration?.financial?.otherFees?.meals;
    if (meals) {
      grandTotal += (meals.fullCampPassesAdult?.quantity || 0) * fullCampAdultRate;
      grandTotal += (meals.fullCampPassesChild?.quantity || 0) * fullCampChildRate;
      grandTotal += (meals.dailyPassesAdult?.quantity || 0) * dailyAdultRate;
      grandTotal += (meals.dailyPassesChild?.quantity || 0) * dailyChildRate;
    } else {
      const legacyMeals = registration?.financial?.mealFees || registration?.mealFees;
      if (legacyMeals) {
        const fullCampAdult = legacyMeals.fullCampPassesAdult || legacyMeals.fullCampPasses?.adult || 0;
        const fullCampChild = legacyMeals.fullCampPassesChild || legacyMeals.fullCampPasses?.child || 0;
        const dailyAdult = legacyMeals.dailyPassesAdult || legacyMeals.dailyPasses?.adult || 0;
        const dailyChild = legacyMeals.dailyPassesChild || legacyMeals.dailyPasses?.child || 0;
        grandTotal += fullCampAdult * fullCampAdultRate;
        grandTotal += fullCampChild * fullCampChildRate;
        grandTotal += dailyAdult * dailyAdultRate;
        grandTotal += dailyChild * dailyChildRate;
      }
    }
  }

  // Other fees (fridge, donations, custom, array items)
  if (registration?.financial?.otherFees) {
    const otherFees = registration.financial.otherFees;
    grandTotal += Number(otherFees.fridge?.cost ?? otherFees.fridgeRental ?? 0) || 0;
    grandTotal += Number(otherFees.donations?.amount ?? otherFees.donation ?? 0) || 0;
    grandTotal += Number(otherFees.customFee?.amount) || 0;
    for (const fee of (otherFees.otherFees || [])) {
      grandTotal += Number(fee?.amount) || 0;
    }
  }

  // Only enabled overrides
  const savedOverrides = registration?.financial?.overrides;
  if (savedOverrides?.accommodation?.enabled) {
    grandTotal += Number(savedOverrides.accommodation.amount) || 0;
  }
  if (savedOverrides?.registration?.enabled) {
    grandTotal += Number(savedOverrides.registration.amount) || 0;
  }
  if (savedOverrides?.mealFees?.enabled) {
    grandTotal += Number(savedOverrides.mealFees.amount) || 0;
  }
  if (savedOverrides?.otherFees?.enabled) {
    grandTotal += Number(savedOverrides.otherFees.amount) || 0;
  }

  return Math.round(grandTotal * 100) / 100;
};

// Computes the outstanding balance: live grand total minus the sum of payments.
// Requires campSettings (settings/camp) so fees are computed from live rates,
// matching the Registration Details page and Accounts Receivable report.
export const computeRegistrationBalance = (registration: any, settings: any): number => {
  const total = computeRegistrationTotal(registration, settings);
  const payments = registration?.paymentHistory;
  const paymentsTotal = Array.isArray(payments)
    ? payments.reduce((sum: number, payment: any) => (typeof payment?.amount === 'number' ? sum + payment.amount : sum), 0)
    : 0;
  return Math.round((total - paymentsTotal) * 100) / 100;
};

export interface BuiltInvoice {
  model: any;
  invoiceNumber: string;
  recipientEmail: string;
  primaryCamperName: string;
  balanceDue: number;
}

// Build the full InvoiceDisplay-ready model for a registration.
export const buildInvoiceDisplayModel = (registration: any, campSettings: any): BuiltInvoice | null => {
  if (!registration) return null;

  const registrationFees = buildInvoiceRegistrationFeeRows(registration, campSettings);
  const paymentAmount = (registration?.paymentHistory || []).reduce(
    (sum: number, payment: any) => sum + (payment.amount || 0),
    0
  );

  const model = {
    id: registration.id,
    primaryCamper: {
      firstName: registration.registrationInfo?.primaryCamperName?.split(' ')[0] || registration.firstName || '',
      lastName: registration.registrationInfo?.primaryCamperName?.split(' ').slice(1).join(' ') || registration.lastName || '',
      email: registration.registrationInfo?.email || registration.email || '',
      address: registration.registrationInfo?.address?.street || '',
      city: registration.registrationInfo?.address?.town || '',
      province: registration.registrationInfo?.address?.province || '',
      postalCode: registration.registrationInfo?.address?.postalCode || '',
    },
    accommodationFees: {
      placeName: registration.financial?.accommodationFees?.placeName || '',
      placeCode: registration.financial?.accommodationFees?.priceCode || '',
      fee: registration.financial?.accommodationFees?.accommodationFee || 0,
      numberOfNights: registration.financial?.accommodationFees?.numberOfNights || 0,
      override: registration.financial?.overrides?.accommodation?.enabled
        ? {
            amount: registration.financial.overrides.accommodation.amount,
            note: registration.financial.overrides.accommodation.notes,
          }
        : undefined,
    },
    registrationFees,
    registrationOverride:
      registration.financial?.overrides?.registration?.amount !== undefined &&
      registration.financial?.overrides?.registration?.amount !== 0
        ? {
            amount: registration.financial.overrides.registration.amount,
            note: registration.financial.overrides.registration.notes,
          }
        : undefined,
    mealFees: {
      fullCampAdult: registration.financial?.otherFees?.meals?.fullCampPassesAdult
        ? {
            quantity: registration.financial.otherFees.meals.fullCampPassesAdult.quantity || 0,
            unitPrice: registration.financial.otherFees.meals.fullCampPassesAdult.unitPrice || 0,
            total: registration.financial.otherFees.meals.fullCampPassesAdult.total || 0,
          }
        : undefined,
      fullCampChild: registration.financial?.otherFees?.meals?.fullCampPassesChild
        ? {
            quantity: registration.financial.otherFees.meals.fullCampPassesChild.quantity || 0,
            unitPrice: registration.financial.otherFees.meals.fullCampPassesChild.unitPrice || 0,
            total: registration.financial.otherFees.meals.fullCampPassesChild.total || 0,
          }
        : undefined,
      dailyAdult: registration.financial?.otherFees?.meals?.dailyPassesAdult
        ? {
            quantity: registration.financial.otherFees.meals.dailyPassesAdult.quantity || 0,
            unitPrice: registration.financial.otherFees.meals.dailyPassesAdult.unitPrice || 0,
            total: registration.financial.otherFees.meals.dailyPassesAdult.total || 0,
          }
        : undefined,
      dailyChild: registration.financial?.otherFees?.meals?.dailyPassesChild
        ? {
            quantity: registration.financial.otherFees.meals.dailyPassesChild.quantity || 0,
            unitPrice: registration.financial.otherFees.meals.dailyPassesChild.unitPrice || 0,
            total: registration.financial.otherFees.meals.dailyPassesChild.total || 0,
          }
        : undefined,
      override:
        registration.financial?.overrides?.mealFees?.amount !== undefined &&
        registration.financial?.overrides?.mealFees?.amount !== 0
          ? {
              amount: registration.financial.overrides.mealFees.amount,
              note: registration.financial.overrides.mealFees.notes,
            }
          : undefined,
    },
    otherFees: {
      fridgeRental: registration.financial?.otherFees?.fridge?.cost || 0,
      donation: registration.financial?.otherFees?.donations?.amount || 0,
      donationNote: registration.financial?.otherFees?.donations?.note || '',
      customFee: registration.financial?.otherFees?.customFee?.amount || 0,
      customFeeNote: registration.financial?.otherFees?.customFee?.note || '',
      otherFees: registration.financial?.otherFees?.otherFees || [],
      override:
        registration.financial?.overrides?.otherFees?.amount !== undefined &&
        registration.financial?.overrides?.otherFees?.amount !== 0
          ? {
              amount: registration.financial.overrides.otherFees.amount,
              note: registration.financial.overrides.otherFees.notes,
            }
          : undefined,
    },
    paymentAmount,
    totalAmount: 0,
    remainingBalance: 0,
  };

  const balanceDue = computeInvoiceBalance(model);
  model.totalAmount = balanceDue + paymentAmount;
  model.remainingBalance = balanceDue;

  const invoiceNumber =
    registration.financial?.invoiceNumber ||
    registration.invoiceNumber ||
    registration.financialRecord?.invoiceNumber ||
    '';

  const recipientEmail = registration.registrationInfo?.email || registration.email || '';
  const primaryCamperName = getPrimaryCamperName(registration);

  return { model, invoiceNumber, recipientEmail, primaryCamperName, balanceDue };
};
